import axios from "axios";
import http from "../http-common";

export const login = async (email, password) => {
  try {
    return await http.post("/users/login", { email, password });
  } catch (error) {
    return error.response.data.message;
  }
};

export const searchUsers = (q) =>
  http.get(`/users/search?q=${encodeURIComponent(q)}&limit=10`).then((r) => r.data);

export const verifyUserEmail = async (userInfo) => {
  try {
    const { data } = await http.post("/users/verify-email", userInfo);
    return data;
  } catch (error) {
    const { response } = error;
    if (response?.data) return response.data;

    return { error: error.message || error };
  }
};

export const resendEmailVerificationToken = async (userId) => {
  try {
    const { data } = await http.post("/users/resend-email-verification-token", { userId });
    return data;
  } catch (error) {
    const { response } = error;
    if (response?.data) return response.data;

    return { error: error.message || error };
  }
};

export const getUserById = async (userId, config = {}) => {
  try {
    const { data } = await http.get(`/users/getuser/${userId}`, config);
    return data;
  } catch (error) {
    console.error("Error in getUserById:", error.response?.data || error.message);
    throw error;
  }
};

// The signed-in user's own record, email and phone included (KIN-91: the public
// getuser route leaves them out). config can carry a token the store lacks yet.
export const getMe = async (config = {}) => {
  const { data } = await http.get("/users/me", config);
  return data;
};

export const getActorFollowersNumber = async (id) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/users/getfollower/${id}`,
      {
        method: "GET",
      }
    );

    return await response.json();
  } catch (error) {
    console.log(error.message);
  }
};

const compressImage = (file, maxDimension = 1600, quality = 0.82) =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(file); return; }
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(objectUrl);
    img.onerror = () => { cleanup(); resolve(file); };
    img.onload = () => {
      cleanup();
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width > height) { height = Math.round(height * maxDimension / width); width = maxDimension; }
        else { width = Math.round(width * maxDimension / height); height = maxDimension; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      const outName = file.name.replace(/\.[^.]+$/, '.jpg');
      canvas.toBlob(
        (blob) => blob ? resolve(new File([blob], outName, { type: 'image/jpeg' })) : resolve(file),
        'image/jpeg',
        quality
      );
    };
    img.src = objectUrl;
  });

// `token` kept for existing call sites; unused now that http auto-attaches
// the current access token to the presign request.
export const uploadSingleFile = async (file) => {
  try {
    const toUpload = await compressImage(file);
    const { data: { url, key } } = await http.get("/users/presign", {
      params: { mimeType: toUpload.type },
    });
    // This PUT goes straight to the S3 presigned URL, not our backend --
    // must stay on plain axios: http's baseURL/withCredentials/auth header
    // would all be wrong here and would break the presigned-URL signature.
    await axios.put(url, toUpload, { headers: { 'Content-Type': toUpload.type } });
    return key;
  } catch (error) {
    console.error("Error uploading file to S3:", error);
    throw error;
  }
};

export const updateUserProfile = async (userId, updatePayload) => {
  try {
    const response = await http.put(`/users/updateProfile/${userId}`, updatePayload);
    return response.data;
  } catch (error) {
    console.error("Error updating User:", error);
    throw error;
  }
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await http.put("/users/changePassword", {
    currentPassword,
    newPassword,
  });
  return response.data;
};

export const deleteAccount = async (userId, token, password) => {
  try {
    const response = await http.delete(`/users/deleteAccount/${userId}`, {
      data: { password },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting account:", error);
    throw error;
  }
};
